import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Building2, Users, FileText, HardDrive, Crown, AlertTriangle, CreditCard, ArrowRight, RefreshCw, CheckCircle, Phone, Mail, MessageCircle, TrendingUp, Calendar, Clock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PageHeader } from '@/components/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const [billingData, setBillingData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    if (!token) return;
    
    try {
      const [billingRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/api/workspace/billing`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/workspace/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (billingRes.ok) {
        const billing = await billingRes.json();
        setBillingData(billing);
      }
      
      if (usageRes.ok) {
        const usage = await usageRes.json();
        setUsageData(usage);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const refreshUsage = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/workspace/usage?refresh=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const usage = await res.json();
        setUsageData(usage);
        toast.success('Utilizzo aggiornato');
      }
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setRefreshing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatStorage = (mb) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(1)} GB`;
    }
    return `${Math.round(mb)} MB`;
  };

  const getPlanBadgeColor = (planCode) => {
    switch (planCode) {
      case 'PRO': return 'bg-purple-100 text-purple-800';
      case 'BASIC': return 'bg-blue-100 text-blue-800';
      case 'TRIAL': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800';
      case 'TRIAL': return 'bg-yellow-100 text-yellow-800';
      case 'EXPIRED': return 'bg-red-100 text-red-800';
      case 'SUSPENDED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Abbonamento"
        subtitle="Piano e fatturazione"
        actions={
          <>
            <Button variant="outline" onClick={refreshUsage} disabled={refreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <Button onClick={() => navigate('/pricing')}>
              Vedi piani
            </Button>
          </>
        }
      />

      {/* Upgrade Recommendation */}
      {billingData?.upgrade_recommendation && (
        <Alert className="border-blue-200 bg-blue-50">
          <TrendingUp className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">Suggerimento</AlertTitle>
          <AlertDescription className="text-blue-700">
            {billingData.upgrade_recommendation.message}
            <Button 
              variant="link" 
              className="ml-2 p-0 h-auto text-blue-600"
              onClick={() => navigate('/pricing')}
            >
              Scopri di più <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Usage Warnings */}
      {usageData?.has_warning && (
        <Alert variant="destructive" className="border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800">Attenzione: Limiti in avvicinamento</AlertTitle>
          <AlertDescription className="text-orange-700">
            Stai raggiungendo i limiti del tuo piano. Considera l'upgrade per evitare interruzioni.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Plan Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-yellow-500" />
                Piano attuale
              </CardTitle>
              <Badge className={getPlanBadgeColor(billingData?.current_plan?.code)}>
                {billingData?.current_plan?.code}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h3 className="text-2xl font-bold">{billingData?.current_plan?.name}</h3>
              <p className="text-sm text-gray-500">{billingData?.current_plan?.description}</p>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Stato:</span>
              <Badge className={getStatusBadgeColor(billingData?.plan_status)}>
                {billingData?.plan_status === 'ACTIVE' && 'Attivo'}
                {billingData?.plan_status === 'TRIAL' && 'Prova'}
                {billingData?.plan_status === 'EXPIRED' && 'Scaduto'}
                {billingData?.plan_status === 'SUSPENDED' && 'Sospeso'}
              </Badge>
            </div>

            {billingData?.current_plan?.pricing?.monthly_price > 0 && (
              <div className="pt-2 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Prezzo mensile</span>
                  <span className="font-medium">{formatPrice(billingData.current_plan.pricing.monthly_price)}</span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-500">Prezzo annuale</span>
                  <span className="font-medium">{formatPrice(billingData.current_plan.pricing.yearly_price)}</span>
                </div>
              </div>
            )}

            {billingData?.plan_status === 'TRIAL' && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-orange-500" />
                  <span>Scade: {formatDate(billingData?.subscription_ends_at)}</span>
                </div>
              </div>
            )}

            {billingData?.next_due_at && (
              <div className="pt-2 border-t">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  <span>Prossimo rinnovo: {formatDate(billingData.next_due_at)}</span>
                </div>
              </div>
            )}
          </CardContent>
          <CardFooter>
            {billingData?.plan_status !== 'ACTIVE' || billingData?.current_plan?.code === 'BASIC' ? (
              <Button className="w-full" onClick={() => navigate('/pricing')}>
                {billingData?.current_plan?.code === 'TRIAL' ? 'Attiva abbonamento' : 'Passa a PRO'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button variant="outline" className="w-full" onClick={() => navigate('/pricing')}>
                Gestisci piano
              </Button>
            )}
          </CardFooter>
        </Card>

        {/* Usage Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Utilizzo risorse</CardTitle>
              <span className="text-xs text-gray-400">
                Ultimo aggiornamento: {usageData?.last_updated ? new Date(usageData.last_updated).toLocaleTimeString('it-IT') : '—'}
              </span>
            </div>
            <CardDescription>
              {usageData?.soft_limits 
                ? 'I limiti sono indicativi (Trial - superamento consentito con avvisi)'
                : 'I limiti sono vincolanti (superamento bloccato)'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Condominiums */}
            <UsageBar
              icon={<Building2 className="h-5 w-5" />}
              label="Condomini"
              data={usageData?.usage_bars?.condominiums}
            />

            {/* Units */}
            <UsageBar
              icon={<Users className="h-5 w-5" />}
              label="Unità"
              data={usageData?.usage_bars?.units}
            />

            {/* Users */}
            <UsageBar
              icon={<Users className="h-5 w-5" />}
              label="Utenti"
              data={usageData?.usage_bars?.users}
            />

            {/* Documents */}
            <UsageBar
              icon={<FileText className="h-5 w-5" />}
              label="Documenti"
              data={usageData?.usage_bars?.documents}
            />

            {/* Storage */}
            <UsageBar
              icon={<HardDrive className="h-5 w-5" />}
              label="Storage"
              data={usageData?.usage_bars?.storage}
              formatValue={(v) => formatStorage(v)}
            />
          </CardContent>
        </Card>
      </div>

      {/* Payment History & Support */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              Storico pagamenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingData?.payments?.length > 0 ? (
              <div className="space-y-3">
                {billingData.payments.map((payment, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                    <div>
                      <p className="font-medium">{formatPrice(payment.amount)}</p>
                      <p className="text-sm text-gray-500">{formatDate(payment.created_at)}</p>
                    </div>
                    <Badge variant={payment.status === 'PAID' ? 'success' : 'secondary'}>
                      {payment.status === 'PAID' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {payment.status}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                Nessun pagamento registrato
              </p>
            )}
          </CardContent>
        </Card>

        {/* Support & Contact */}
        <Card>
          <CardHeader>
            <CardTitle>Assistenza</CardTitle>
            <CardDescription>
              Hai domande sul tuo piano o sulla fatturazione?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button variant="outline" className="w-full justify-start" asChild>
              <a href={`mailto:${billingData?.support_email}`}>
                <Mail className="h-4 w-4 mr-3" />
                {billingData?.support_email}
              </a>
            </Button>
            <Button variant="outline" className="w-full justify-start" asChild>
              <a href={`tel:${billingData?.support_phone}`}>
                <Phone className="h-4 w-4 mr-3" />
                {billingData?.support_phone}
              </a>
            </Button>
            <Button variant="outline" className="w-full justify-start" asChild>
              <a href="https://wa.me/393520498295" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-4 w-4 mr-3" />
                WhatsApp
              </a>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Available Plans Preview */}
      {billingData?.available_plans && (
        <Card>
          <CardHeader>
            <CardTitle>Piani disponibili</CardTitle>
            <CardDescription>
              Confronta i piani e scegli quello più adatto alle tue esigenze
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {billingData.available_plans.map((plan) => (
                <div 
                  key={plan.code}
                  className={`p-4 rounded-lg border-2 ${
                    plan.code === billingData?.current_plan?.code 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{plan.name}</h4>
                    {plan.code === billingData?.current_plan?.code && (
                      <Badge>Piano attuale</Badge>
                    )}
                  </div>
                  <p className="text-2xl font-bold mb-2">{formatPrice(plan.pricing.monthly_price)}/mese</p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>{plan.limits.max_condominiums} condomini</li>
                    <li>{plan.limits.max_units.toLocaleString()} unità</li>
                    <li>{Math.round(plan.limits.max_storage_mb / 1024)} GB storage</li>
                  </ul>
                  {plan.code !== billingData?.current_plan?.code && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 w-full"
                      onClick={() => navigate('/pricing')}
                    >
                      Seleziona
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Usage Bar Component
function UsageBar({ icon, label, data, formatValue }) {
  if (!data) return null;
  
  const { current, limit, percent, warning, exceeded } = data;
  
  const getProgressColor = () => {
    if (exceeded) return 'bg-red-500';
    if (warning) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  const displayCurrent = formatValue ? formatValue(current) : current.toLocaleString();
  const displayLimit = formatValue ? formatValue(limit) : limit.toLocaleString();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={warning ? 'text-orange-500' : 'text-gray-400'}>{icon}</span>
          <span className="font-medium">{label}</span>
          {warning && !exceeded && (
            <Badge variant="outline" className="text-orange-600 border-orange-300 text-xs">
              Quasi pieno
            </Badge>
          )}
          {exceeded && (
            <Badge variant="destructive" className="text-xs">
              Limite raggiunto
            </Badge>
          )}
        </div>
        <span className="text-sm text-gray-500">
          {displayCurrent} / {displayLimit}
        </span>
      </div>
      <div className="relative">
        <Progress value={Math.min(percent, 100)} className="h-2" />
        <div 
          className={`absolute top-0 left-0 h-full rounded-full transition-all ${getProgressColor()}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <div className="flex justify-between text-xs text-gray-400">
        <span>{percent.toFixed(0)}% utilizzato</span>
        <span>{data.remaining?.toLocaleString() || (limit - current).toLocaleString()} rimanenti</span>
      </div>
    </div>
  );
}
